import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import _ from 'lodash';
import { Select, ConfigProvider, Button, Tag, Switch } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { navbarHeight, sidebarWidth } from '../../constants/gridConfig';
import { PANEL_LIGHT_BG, PRIMARY_DARK, defaultTableConfig, defaultTableConfiguration, getTableConfigControls, isTableColumnsConfigurable, getTablePreviewLayout, getTablePreviewSample } from '../../constants/constants';
import { hexToRgba } from '../../utils/theme';
import { findTableStyle } from '../../utils/styles';
import { getPlanningScreenshotStyles } from '../../requests/planningScreenshotStyles';
import TableConfigBody from '../../shared/tables/ui/TableConfigBody';
import CustomTablePreview from '../../shared/tables/ui/CustomTablePreview';
import {
  mergeColumns,
  moveColumn,
  moveColumnByLabel,
  setColumnWidth,
  toggleColumnHidden,
  cycleColumnAlign,
  toColumnOverrides,
} from '../../shared/tables/tableConfigColumns';
import { awaitsRowKindsRefresh } from '../../shared/tables/tableRowKinds';
import { zoneSplitTableName } from '../../shared/tables/zoneSplit';
import {
  clearRowStyle, findRowStyle, patchRowStyle, resetRowStyle, setRowStyle,
} from '../../shared/tables/tableRowStyle';
import {
  columnWidthPercents, reorderCustomTableColumns, setColumnWidthPercent,
} from '../../shared/tables/customTableColumns';
import {
  alignTableColumn, customRowStyles, fetchTableData, mergeTableCells, tablePlainText, unmergeTableCells,
} from '../../utils/table';
import { replaceDraftPlainText, transformTextToEditorState } from '../../utils/dataSync';
import { MIN_COLUMN_WIDTH } from '../Table';
import { tableConfigAntdWidgets, createAntdDragAdapter } from './tableConfigAntdWidgets';
import { getSectionRecursiveMeta } from '../../utils/recursiveSections';
import BoxFormControlsPortal from './BoxFormControlsPortal';
import TableRowStylePanel from './TableRowStylePanel';
import { resolveTableConfig } from '../../shared/tables/tableDescriptors';

const DRAWER_WIDTH = 650;
// Valeur sentinelle « aucun style choisi » : le backend applique alors le style
// marqué par défaut pour le métier.
const DEFAULT_PLANNING_STYLE = '__default__';

const DrawerBody = ({ box }) => {
  const { t } = useTranslation();
  const {
    applyTableConfig,
    fetchTableCatalog,
    bpuCapabilities,
    currentTemplate,
    zoneSplitTables,
    sections,
    setTableSplit,
    currentDocument,
    fetchTable,
    updateBox,
    cacheTableRaw,
  } = useStore(
    ({
      applyTableConfig,
      fetchTableCatalog,
      bpuCapabilities,
      currentTemplate,
      zoneSplitTables,
      sections,
      setTableSplit,
      currentDocument,
      fetchTable,
      updateBox,
      cacheTableRaw,
    }) => ({
      applyTableConfig,
      fetchTableCatalog,
      bpuCapabilities,
      currentTemplate,
      zoneSplitTables,
      sections,
      setTableSplit,
      currentDocument,
      fetchTable,
      updateBox,
      cacheTableRaw,
    })
  );
  const [splitting, setSplitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Style du tableau, résolu comme dans le document (cf. Table) : l'aperçu se
  // rend avec.
  const boxTableStyle = useStore((state) =>
    findTableStyle(state.configuration, state.tmp, box.tableStyleId)
  );
  const configuration = useStore((state) => state.configuration);
  const previewStyle = useMemo(
    () => ({ tableStyle: { ...defaultTableConfiguration, ...boxTableStyle }, configuration }),
    [boxTableStyle, configuration]
  );

  // Découpage par zone : proposé aux tableaux que le serveur annonce découpables
  // par zone, sauf dans une section récursive où la boucle impose déjà la découpe.
  const isInRecursiveSection = useMemo(() => {
    if (box.content?.recursiveRowId != null) return true;
    if (!box.fromSectionId) return false;
    const section = sections?.find((s) => String(s.id) === String(box.fromSectionId));
    return getSectionRecursiveMeta(section).isRecursive;
  }, [sections, box.fromSectionId, box.content?.recursiveRowId]);
  const canSplitByZone =
    !isInRecursiveSection &&
    Array.isArray(zoneSplitTables) &&
    zoneSplitTables.includes(box.tableName);

  const toggleSplit = async (checked) => {
    setSplitting(true);
    try {
      await setTableSplit(box.id, checked);
    } finally {
      setSplitting(false);
    }
  };

  // Tableau historique : pas de config par défaut, seulement la présentation.
  // Relue dans le modèle courant : le premier enregistrement la réécrit ainsi.
  const initialConfig =
    resolveTableConfig(box.tableName, box.tableConfig || defaultTableConfig(box.tableName)) || { columns: [] };
  const [cfg, setCfg] = useState(initialConfig);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const controls = getTableConfigControls(box.tableName);
  const columnsConfigurable = isTableColumnsConfigurable(box.tableName);
  const hasPlanningStyle = controls.some((c) => c.type === 'planning-style');
  // null tant que la liste n'a pas répondu : sans ça, un style déjà choisi
  // s'afficherait « supprimé » le temps de la requête.
  const [planningStyles, setPlanningStyles] = useState(null);
  const templateWork = currentTemplate?.work;
  // Capabilities backend (ex. bpu { additional_work }) : masquent certaines options.
  const capabilities = bpuCapabilities;

  // Clé des axes donnée : recharge le catalogue quand un axe change (ex. la
  // variante Prestations change le jeu de colonnes), pas sur une édition de colonne.
  // Le style en est exclu : il ne change que des couleurs, jamais les colonnes.
  const axesKey = controls
    .filter((c) => c.type !== 'planning-style')
    .map((c) => cfg[c.field])
    .join('|');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTableCatalog(box, cfg).then((res) => {
      if (cancelled) return;
      setCatalog(res?.columns || []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box.tableName, axesKey]);

  // Styles de screenshot planning proposés à l'utilisateur. Chargés une fois :
  // la liste ne dépend pas des axes du tableau.
  useEffect(() => {
    if (!hasPlanningStyle) return undefined;
    let cancelled = false;
    getPlanningScreenshotStyles(templateWork || null).then((res) => {
      if (!cancelled) setPlanningStyles(res?.styles || []);
    });
    return () => {
      cancelled = true;
    };
  }, [hasPlanningStyle, templateWork]);

  const columns = useMemo(() => mergeColumns(catalog, cfg.columns), [catalog, cfg.columns]);

  // Commit debouncé pour éviter un refetch par frappe (largeur) ou par hover de
  // drag en mode proposition. cfgRef garde la dernière valeur pour le flush.
  const timer = useRef(null);
  const cfgRef = useRef(cfg);
  const commit = useCallback(
    (nextCfg, { immediate = false } = {}) => {
      cfgRef.current = nextCfg;
      setCfg(nextCfg);
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      if (immediate) {
        applyTableConfig(box.id, nextCfg);
        return;
      }
      timer.current = setTimeout(() => {
        // IMPORTANT : remettre à null une fois le commit écoulé, sinon la garde de
        // resync (`if (timer.current) return`) considère un commit Drawer en cours
        // pour toujours -> une édition d'en-tête au canvas ne se répercuterait plus
        // dans le Drawer.
        timer.current = null;
        applyTableConfig(box.id, cfgRef.current);
      }, 300);
    },
    [applyTableConfig, box.id]
  );

  // Applique immédiatement l'état courant (fin de drag) : évite de perdre le
  // commit debouncé si le drawer se ferme dans les 300 ms.
  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    applyTableConfig(box.id, cfgRef.current);
  }, [applyTableConfig, box.id]);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  // Resync quand la config change DE L'EXTÉRIEUR : édition au canvas (intitulé /
  // largeur) pendant que le Drawer est ouvert -> box.tableConfig bouge, on aligne
  // l'état local. On saute si un commit Drawer est en attente (timer) pour ne pas
  // écraser une édition en cours ; à l'échéance du debounce, box.tableConfig ==
  // cfgRef -> pas de resync superflu.
  useEffect(() => {
    // Relue comme à l'ouverture : comparée brute, une ancienne config semblait
    // toujours différente et remplaçait la version relue (logo des formations
    // réaffiché alors qu'il était masqué).
    const incoming = box.tableConfig && resolveTableConfig(box.tableName, box.tableConfig);
    if (!incoming || timer.current) return;
    if (JSON.stringify(incoming) !== JSON.stringify(cfgRef.current)) {
      cfgRef.current = incoming;
      setCfg(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box.tableConfig]);

  // Lignes d'exemple de la variante ; sans forme, rien à fusionner ni styler.
  // Découpée par zone, la box lit la version « par zones » (titre de zone).
  const previewLayout = getTablePreviewLayout(
    box.content?.shouldSplit ? zoneSplitTableName(box.tableName) : box.tableName,
    cfg,
  );
  const staleRowKinds =
    currentDocument?.type !== 'template' && awaitsRowKindsRefresh(cfg, box.content?.data);

  // Même rafraîchissement que le bouton du tableau.
  const refreshTable = async () => {
    setRefreshing(true);
    try {
      await fetchTableData({
        fetchStoreFunc: fetchTable,
        updateBoxFunc: updateBox,
        box,
        split: box.content?.shouldSplit,
        recursiveRowId: box.content?.recursiveRowId,
        refresh: true,
        onRawTable: (table) => cacheTableRaw(box.id, table),
      });
    } finally {
      setRefreshing(false);
    }
  };

  // Ligne cliquée dans l'aperçu (`{ label, kind }`) : son style se règle dans
  // le panel de droite, pour toutes les lignes de son type ou de son libellé.
  // Rendu seul, comme la fusion : applyTableConfig ne relit pas la table.
  const [selectedRow, setSelectedRow] = useState(null);
  // Colonne de la case cliquée, et portée des réglages du panel : la ligne
  // entière ou cette colonne seulement (cf. cellStyleEntry).
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [styleScope, setStyleScope] = useState('row');
  const columnScope = styleScope === 'column' ? selectedColumn : null;
  const commitRowStyles = (rowStyles) => commit({ ...cfgRef.current, rowStyles }, { immediate: true });

  // Écrit la liste complète des overrides (tous les labels du catalogue) à partir
  // de l'état d'affichage courant, en réassignant un ordre séquentiel.
  const commitColumns = (nextColumns, options) =>
    commit({ ...cfgRef.current, columns: toColumnOverrides(nextColumns) }, options);

  const setField = (field, value) => commit({ ...cfg, [field]: value });

  const toggleHidden = (label) =>
    commitColumns(toggleColumnHidden(columns, label));

  // Renommage colonne : chaîne vide → retour au nom d'origine (customLabel effacé).
  const setCustomLabel = (label, value) =>
    commitColumns(
      columns.map((c) =>
        c.label === label ? { ...c, customLabel: value?.length ? value : undefined } : c
      )
    );

  const resetLabel = (label) =>
    commitColumns(columns.map((c) => (c.label === label ? { ...c, customLabel: undefined } : c)));

  // Réinitialise tous les intitulés custom d'un coup (retour aux noms d'origine).
  const resetAllLabels = () =>
    commitColumns(columns.map((c) => ({ ...c, customLabel: undefined })));

  const setWidth = (label, value) => commitColumns(setColumnWidth(columns, label, value));

  const cycleAlign = (label) => commitColumns(cycleColumnAlign(columns, label), { immediate: true });

  const move = (index, dir) => {
    const next = moveColumn(columns, index, dir);
    if (next !== columns) commitColumns(next);
  };

  // Réordonnancement live pendant le drag (setCfg immédiat, applyTableConfig
  // debouncé). On repart de cfgRef : pendant un drag, `columns` peut dater du
  // rendu précédent.
  const moveByDrag = useCallback(
    (draggedLabel, overLabel) => {
      const current = mergeColumns(catalog, cfgRef.current.columns);
      const next = moveColumnByLabel(current, draggedLabel, overLabel);
      if (next === current) return;
      commit({ ...cfgRef.current, columns: toColumnOverrides(next) });
    },
    [catalog, commit]
  );

  const dragAdapter = useMemo(
    () => createAntdDragAdapter({ onMoveByDrag: moveByDrag, onDropEnd: flush }),
    [moveByDrag, flush]
  );

  // Applique un style planning : on copie ses couleurs dans la config (le
  // document reste figé si l'admin modifie le style ensuite) et on mémorise son
  // empreinte pour pouvoir signaler qu'une version plus récente existe.
  const applyPlanningStyle = (style) =>
    commit({
      ...cfg,
      styleId: style?.id ?? null,
      styleName: style?.name ?? null,
      styleDigest: style?.theme_digest ?? null,
      theme: style?.theme ?? null,
    });

  const renderPlanningStyleControl = (control) => {
    const loaded = planningStyles !== null;
    const availableStyles = planningStyles || [];
    const selected = availableStyles.find((s) => s.id === cfg.styleId);
    // Style supprimé en admin : le tableau garde ses couleurs, on le dit sans
    // rien changer au rendu. Conclusion possible seulement une fois la liste
    // reçue, sinon tout style paraîtrait supprimé pendant le chargement.
    const deleted = loaded && cfg.styleId != null && !selected;
    const outdated = selected && cfg.styleDigest && selected.theme_digest !== cfg.styleDigest;
    const options = [
      { value: DEFAULT_PLANNING_STYLE, label: t('planning-style-default') },
      ...availableStyles.map((style) => ({
        value: style.id,
        label: style.work ? `${style.name} — ${t(style.work)}` : style.name,
      })),
    ];
    if (cfg.styleId != null && !selected) {
      // Entrée de repli tant que la liste charge, puis pour un style supprimé :
      // sans elle le Select afficherait une valeur vide alors qu'un thème est
      // bien appliqué.
      options.push({
        value: cfg.styleId,
        label: deleted
          ? `${cfg.styleName || t('planning-style')} (${t('planning-style-deleted')})`
          : cfg.styleName || t('planning-style'),
      });
    }

    return (
      <div key={control.field}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>{t(control.labelKey)}</div>
        <ConfigProvider theme={{ token: { controlItemBgActive: hexToRgba(PRIMARY_DARK, 0.12) } }}>
          <Select
            style={{ width: '100%' }}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            value={cfg.styleId ?? DEFAULT_PLANNING_STYLE}
            onChange={(value) => {
              if (value === DEFAULT_PLANNING_STYLE) {
                applyPlanningStyle(null);
                return;
              }
              const style = availableStyles.find((s) => s.id === value);
              // Entrée de repli (style supprimé ou liste non chargée) : inerte,
              // sinon on effacerait les couleurs figées du document.
              if (style) applyPlanningStyle(style);
            }}
            options={options}
          />
        </ConfigProvider>
        {deleted && (
          <div style={{ marginTop: 8 }}>
            <Tag color='warning'>{t('planning-style-deleted-hint')}</Tag>
          </div>
        )}
        {outdated && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: PRIMARY_DARK }}>{t('planning-style-outdated')}</span>
            <Button size='small' onClick={() => applyPlanningStyle(selected)}>
              {t('planning-style-refresh')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {canSplitByZone && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{t('table-split-by-zone')}</div>
            <div style={{ fontSize: 12, color: 'rgba(11, 34, 57, 0.65)' }}>
              {t('table-split-by-zone-hint')}
            </div>
          </div>
          <Switch checked={!!box.content?.shouldSplit} loading={splitting} onChange={toggleSplit} />
        </div>
      )}
      <TableConfigBody
        t={t}
        controls={controls}
        config={cfg}
        capabilities={capabilities}
        columns={columns}
        // Pas de colonnes à régler sans données : tableau historique d'un modèle
        // (son catalogue vient de la réponse de l'API) ou table vide.
        columnsConfigurable={columnsConfigurable && (loading || catalog.length > 0)}
        columnsUnavailableNote={!loading && catalog.length === 0 ? t('table-columns-unavailable') : null}
        previewLayout={previewLayout}
        previewSample={getTablePreviewSample(box.tableName, cfg)}
        previewStyle={previewStyle}
        mergeOptions={cfg.merge}
        previewNotice={staleRowKinds && (
          <>
            <span>{t('table-merge-refresh-hint')}</span>
            <Button size='small' loading={refreshing} onClick={refreshTable}>
              {t('table-merge-refresh')}
            </Button>
          </>
        )}
        // La fusion automatique ne se règle plus ici (autre mécanisme à venir) ;
        // les exceptions déjà choisies au canvas restent listées.
        showMergeSwitches={false}
        // Fusion seule : applyTableConfig la pose sur la box sans relire la table.
        onMergeChange={(merge) => commit({ ...cfgRef.current, merge }, { immediate: true })}
        onSelectedRowChange={(row, column) => {
          setSelectedRow(row);
          setSelectedColumn(column ?? null);
        }}
        loading={loading}
        widgets={tableConfigAntdWidgets}
        dragAdapter={dragAdapter}
        // Le style planning n'existe que dans le document : l'écran partagé ne
        // sait pas le rendre, il délègue ce seul contrôle.
        renderControl={(control) =>
          (control.type === 'planning-style' ? renderPlanningStyleControl(control) : null)
        }
        onAxisChange={setField}
        onToggleHidden={toggleHidden}
        onRename={setCustomLabel}
        onResetName={resetLabel}
        onResetAllNames={resetAllLabels}
        onWidth={setWidth}
        onAlign={cycleAlign}
        onMove={move}
      />
      {/* Le panel du tableau règle le tableau du document, caché par le Drawer
          (cf. Table) : à sa place, le style du tableau et de ses lignes. */}
      <BoxFormControlsPortal title={t('table')} order={1}>
        <TableRowStylePanel
          boxId={box.id}
          tableFont={{
            family: previewStyle.tableStyle.fontFamilyCells || configuration.fontFamily,
            size: previewStyle.tableStyle.fontSizeCells || configuration.fontSize,
          }}
          rowsStylable={!!previewLayout}
          row={selectedRow}
          rule={findRowStyle(cfg.rowStyles, selectedRow)}
          column={selectedColumn}
          scope={styleScope}
          onScopeChange={setStyleScope}
          onChange={(patch) => commitRowStyles(setRowStyle(cfgRef.current.rowStyles, selectedRow, patch, columnScope))}
          onReset={() => commitRowStyles(resetRowStyle(cfgRef.current.rowStyles, selectedRow, columnScope))}
        />
      </BoxFormControlsPortal>
    </>
  );
};

// Valeurs d'alignement du document (PopMenu : left | center | end | justify)
// ramenées au cycle de l'écran de configuration (start | center | end).
const configAlign = (align) => (align === 'center' || align === 'end' ? align : 'start');
const ALIGN_CYCLE = ['start', 'center', 'end'];
// Texte d'un titre tel qu'il a été tapé : tablePlainText le rogne, et l'espace
// tapé avant une pause disparaîtrait du champ dès l'écriture du titre.
const titleText = (title) => transformTextToEditorState(title).getCurrentContent().getPlainText();

/**
 * Tableau custom (non variable) : mêmes réglages que pour un tableau variable,
 * mais écrits directement dans le tableau (`box.content`), sa seule source —
 * rien à relire. Les colonnes se renomment, se redimensionnent, s'alignent et
 * se déplacent ; l'aperçu montre ses vraies lignes, où l'on fusionne et d'où
 * l'on règle le style d'une ligne ou d'une case (rangé sur la ligne, cf.
 * customRowStyles). Pas de colonne masquée : on la supprime.
 */
const CustomTableDrawerBody = ({ box }) => {
  const { t } = useTranslation();
  const updateBox = useStore(({ updateBox }) => updateBox);
  const configuration = useStore((state) => state.configuration);
  const boxTableStyle = useStore((state) =>
    findTableStyle(state.configuration, state.tmp, box.tableStyleId)
  );
  const previewStyle = useMemo(
    () => ({ tableStyle: { ...defaultTableConfiguration, ...boxTableStyle }, configuration }),
    [boxTableStyle, configuration]
  );

  const boxColumns = box.content.columns;
  const data = box.content.data;

  // Intitulés en cours de frappe : écrits dans le titre de la colonne après une
  // pause, comme les réglages du Drawer d'un tableau variable.
  const [typedTitles, setTypedTitles] = useState({});
  const pendingTitles = useRef({});
  const titleTimer = useRef(null);
  const flushTitles = useCallback(() => {
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = null;
    const pending = pendingTitles.current;
    pendingTitles.current = {};
    if (Object.keys(pending).length === 0) return;
    updateBox(box.id, (b) => {
      Object.entries(pending).forEach(([key, text]) => {
        const column = b.content.columns.find((c) => c.key === key);
        if (column) column.title = replaceDraftPlainText(column.title, text);
      });
    });
    setTypedTitles((prev) => _.omit(prev, Object.keys(pending)));
  }, [box.id, updateBox]);
  // Drawer refermé pendant la pause : l'intitulé tapé n'est pas perdu.
  const flushTitlesRef = useRef(flushTitles);
  flushTitlesRef.current = flushTitles;
  useEffect(() => () => flushTitlesRef.current(), []);

  const rename = (key, text) => {
    pendingTitles.current[key] = text;
    setTypedTitles((prev) => ({ ...prev, [key]: text }));
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(flushTitles, 300);
  };

  // Ordre des colonnes pendant un glisser : posé au dépôt seulement. Passer
  // au-dessus d'une fusion ne doit pas la défaire (cf. reorderCustomTableColumns).
  const [dragOrder, setDragOrder] = useState(null);
  const dragOrderRef = useRef(null);

  const reorder = useCallback((orderedKeys) => {
    updateBox(box.id, (b) => {
      Object.assign(b.content, reorderCustomTableColumns(b.content, orderedKeys));
    });
  }, [box.id, updateBox]);

  const moveByDrag = useCallback((draggedKey, overKey) => {
    const current = (dragOrderRef.current ?? boxColumns.map((c) => c.key)).map((key) => ({ label: key }));
    const next = moveColumnByLabel(current, draggedKey, overKey);
    if (next === current) return;
    dragOrderRef.current = next.map((c) => c.label);
    setDragOrder(dragOrderRef.current);
  }, [boxColumns]);

  const dropEnd = useCallback(() => {
    const order = dragOrderRef.current;
    dragOrderRef.current = null;
    setDragOrder(null);
    if (order) reorder(order);
  }, [reorder]);

  const dragAdapter = useMemo(
    () => createAntdDragAdapter({ onMoveByDrag: moveByDrag, onDropEnd: dropEnd }),
    [moveByDrag, dropEnd]
  );

  const percents = columnWidthPercents(boxColumns);
  const columnName = (col, idx) =>
    typedTitles[col.key] ?? (tablePlainText(col.title) || t('table-column-number', { number: idx + 1 }));
  const orderedColumns = dragOrder
    ? dragOrder.map((key) => boxColumns.find((c) => c.key === key)).filter(Boolean)
    : boxColumns;
  // Format de l'écran de configuration : `label` est l'identifiant de la
  // colonne (sa clé), l'intitulé se tape dans `customLabel`.
  const columns = orderedColumns.map((col) => {
    const idx = boxColumns.indexOf(col);
    return {
      label: col.key,
      name: columnName(col, idx),
      customLabel: typedTitles[col.key] ?? titleText(col.title),
      width: percents[idx],
      align: configAlign(col.textAlign),
      type: col.type,
    };
  });

  const setWidth = (key, value) => {
    if (value == null) return;
    updateBox(box.id, (b) => {
      const widths = setColumnWidthPercent(b.content.columns, key, value, MIN_COLUMN_WIDTH);
      b.content.columns.forEach((col, idx) => {
        col.width = widths[idx];
      });
    });
  };

  const cycleAlign = (key) => {
    const column = boxColumns.find((c) => c.key === key);
    if (!column) return;
    const next = ALIGN_CYCLE[(ALIGN_CYCLE.indexOf(configAlign(column.textAlign)) + 1) % ALIGN_CYCLE.length];
    updateBox(box.id, (b) => alignTableColumn(b.content, key, 'textAlign', next));
  };

  const move = (index, direction) => {
    const keys = boxColumns.map((c) => c.key);
    const target = index + direction;
    if (target < 0 || target >= keys.length) return;
    [keys[index], keys[target]] = [keys[target], keys[index]];
    reorder(keys);
  };

  // Aperçu : texte nu des cases (Draft dans le document).
  const previewColumns = useMemo(() => {
    const widths = columnWidthPercents(boxColumns);
    return boxColumns.map((col, idx) => ({
      key: col.key,
      name: tablePlainText(col.title),
      width: widths[idx],
      align: col.textAlign,
      headerAlign: col.headerTextAlign,
      type: col.type,
    }));
  }, [boxColumns]);
  const previewRows = useMemo(() => data.map((row) => ({
    key: row.key,
    cells: Object.fromEntries(boxColumns.map((col) => [col.key, tablePlainText(row[col.key])])),
    align: Object.fromEntries(boxColumns.map((col) => [col.key, row[`textAlign_${col.key}`]])),
  })), [data, boxColumns]);
  const rowStyles = useMemo(() => customRowStyles(data), [data]);

  // Case choisie dans l'aperçu : son style, ou celui de sa ligne, se règle dans
  // le panel de droite.
  const [selected, setSelected] = useState(null); // { rowKey, columnKey }
  const [styleScope, setStyleScope] = useState('row');
  const selectedRowIndex = selected ? data.findIndex((row) => row.key === selected.rowKey) : -1;
  const selectedRow = selectedRowIndex >= 0 ? data[selectedRowIndex] : null;
  const selectedColumnIndex = selected ? boxColumns.findIndex((c) => c.key === selected.columnKey) : -1;
  const columnScope = styleScope === 'column' && selectedColumnIndex >= 0 ? selected.columnKey : null;
  const writeRowStyle = (rowStyle) => updateBox(box.id, (b) => {
    const row = b.content.data.find((r) => r.key === selected.rowKey);
    if (!row) return;
    if (rowStyle) row.rowStyle = rowStyle;
    else delete row.rowStyle;
  });

  return (
    <>
      <TableConfigBody
        t={t}
        controls={[]}
        config={{}}
        columns={columns}
        columnsConfigurable={boxColumns.length > 0}
        loading={false}
        widgets={tableConfigAntdWidgets}
        dragAdapter={dragAdapter}
        preview={(
          <CustomTablePreview
            t={t}
            widgets={tableConfigAntdWidgets}
            columns={previewColumns}
            rows={previewRows}
            mergedCells={box.content.mergedCells}
            rowStyles={rowStyles}
            previewStyle={previewStyle}
            onSelect={setSelected}
            onMerge={(keys) => updateBox(box.id, (b) => mergeTableCells(b.content, keys))}
            onUnmerge={(keys) => updateBox(box.id, (b) => unmergeTableCells(b.content, keys))}
          />
        )}
        onRename={rename}
        onWidth={setWidth}
        onAlign={cycleAlign}
        onMove={move}
      />
      <BoxFormControlsPortal title={t('table')} order={1}>
        <TableRowStylePanel
          boxId={box.id}
          tableFont={{
            family: previewStyle.tableStyle.fontFamilyCells || configuration.fontFamily,
            size: previewStyle.tableStyle.fontSizeCells || configuration.fontSize,
          }}
          rowsStylable
          row={selectedRow ? { caption: t('table-row-number', { number: selectedRowIndex + 1 }) } : null}
          rule={selectedRow?.rowStyle ?? null}
          column={selectedColumnIndex >= 0 ? selected.columnKey : null}
          columnName={selectedColumnIndex >= 0 ? columnName(boxColumns[selectedColumnIndex], selectedColumnIndex) : null}
          scope={styleScope}
          onScopeChange={setStyleScope}
          onChange={(patch) => writeRowStyle(patchRowStyle(selectedRow.rowStyle, patch, columnScope))}
          onReset={() => writeRowStyle(clearRowStyle(selectedRow.rowStyle, columnScope))}
        />
      </BoxFormControlsPortal>
    </>
  );
};

const TableConfigDrawer = () => {
  const { t } = useTranslation();
  const tableConfigBoxId = useStore(({ tableConfigBoxId }) => tableConfigBoxId);
  const selectedBoxId = useStore(({ selectedBoxId }) => selectedBoxId);
  const boxes = useStore(({ boxes }) => boxes);
  const closeTableConfigDrawer = useStore(({ closeTableConfigDrawer }) => closeTableConfigDrawer);

  const box = useMemo(
    () => boxes.find((b) => b.id === tableConfigBoxId) || null,
    [boxes, tableConfigBoxId]
  );

  // Tout tableau a son configurateur (colonnes, fusion, style des lignes) ; les
  // variantes n'existent que pour les tableaux paramétrables.
  const open = !!box;

  // Fermer dès que la box n'est plus sélectionnée (clic en dehors, sélection
  // d'une autre box...) : selectedBoxId est remis à null à la désélection,
  // mais rien ne nettoie tableConfigBoxId spontanément.
  useEffect(() => {
    if (tableConfigBoxId && selectedBoxId !== tableConfigBoxId) {
      closeTableConfigDrawer();
    }
  }, [selectedBoxId, tableConfigBoxId, closeTableConfigDrawer]);

  return (
    // Conteneur de clipping : s'arrête exactement au bord gauche du panel de
    // droite (right: sidebarWidth) et masque le débordement. Le Drawer glisse à
    // l'intérieur : à la fermeture il se cache en étant DÉCOUPÉ à ce bord, au lieu
    // de balayer par-dessus le panel (qui n'est qu'en zIndex 15). On garde
    // zIndex 10002 (au-dessus de FloatyStylingWrapper 9998 / AdderMenu 10001, qui
    // recouvrent la zone du document) sans avoir à remonter le panel — remonter le
    // panel ferait passer les popups antd (~1050) derrière lui.
    <div
      style={{
        position: 'fixed',
        top: navbarHeight,
        left: 0,
        right: sidebarWidth,
        height: `calc(100vh - ${navbarHeight}px)`,
        overflow: 'hidden',
        zIndex: 10002,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: DRAWER_WIDTH,
          backgroundColor: PANEL_LIGHT_BG,
          borderLeft: '1px solid rgba(11, 34, 57, 0.25)',
          boxShadow: '-8px 0 24px rgba(11, 34, 57, 0.12)',
          transform: open ? 'translateX(0)' : `translateX(${DRAWER_WIDTH + 40}px)`,
          transition: 'transform 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid rgba(11, 34, 57, 0.1)',
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 700, color: PRIMARY_DARK }}>{t('table-configure')}</span>
          <FontAwesomeIcon
            icon={icon({ name: 'chevron-right', style: 'solid' })}
            onClick={closeTableConfigDrawer}
            style={{ cursor: 'pointer', color: PRIMARY_DARK }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {open && (box.tableName
            ? <DrawerBody key={box.id} box={box} />
            : <CustomTableDrawerBody key={box.id} box={box} />)}
        </div>
      </div>
    </div>
  );
};

export default TableConfigDrawer;
